import * as React from 'react'
import { Props as ModalProps, SettingsBodyApplyModal, SettingsBodyDisableModal } from '../common/Modal/SettingsBodyModal'
import { Props as EnvironmentSelectorProps, SettingsBodyEnvironmentSelector } from '../common/EnvironmentSelector/SettingsBodyEnvironmentSelector'
import { Props as ResourcesSearchBarProps, SettingsBodyResourcesListHeader } from '../common/ResourcesList/Header/SettingsBodyResourcesListHeader'
import { Props as ResourcesListProps, SettingsBodyResourcesListBody } from '../common/ResourcesList/Body/SettingsBodyResourcesListBody'
import { ModalProps as EnableNewCHAppsProps } from '../common/EnableCheckbox/NewAppsCloudHubEnableCheckbox'
import { Props as ToastProps, SettingsBodyToast } from '../common/Toast/SettingsBodyToast'
import { SettingsApplication } from '../common/model'
import { SettingsService } from '../../util/SettingsService'
import { Environment } from '../../../../remote-api/CoreServicesRemoteApi'
import { ApplyStatus, EnableMonitoringStatus } from './model'
import './CloudHubSettingsPage.css'
import { NewAppsCloudHubEnableCheckbox } from '../common/EnableCheckbox/NewAppsCloudHubEnableCheckbox';

export type ValueProps = {
  organizationId: string,
  environmentsLoading: boolean,
  isTitanium: boolean,
  environments?: Environment[],
  selectedEnvironment?: Environment,
  isLoadingResources: boolean,
  isLoadingCHSettings: boolean,
  isChangingCHSettings: boolean,
  isNewAppsAgentEnabled?: boolean,
  filteredResources?: SettingsApplication[],
  applyStatus?: ApplyStatus,
  applyEnableMonitoringStatus?: EnableMonitoringStatus,
  query?: string
}

export type EventProps = {
  fetchEnvironments: () => void,
  selectEnvironment: (envId: string) => void,
  fetchResources: (envId: string, changedEnvironment: boolean) => void,
  filterResources: (input: string) => void,
  checkedMonitoring: (id: string) => void,
  applyMonitoringChanges: (amEnabled: boolean, app: SettingsApplication) => void
  fetchCloudHubSettings: (enable: boolean) => void
  postCloudHubSettings: (enable: boolean) => void
}

export type Props = ValueProps & EventProps

export type State = {
  applyModalOpened: boolean,
  disableModalOpened: boolean,
  amEnabled: boolean,
  selectedApp?: SettingsApplication,
  lastSelectedEnvironment?: string,
  intervalId?: number,
  resources?: SettingsApplication[]
}

const intervalTime = 10000

export class CloudHubSettingsPage extends React.Component<Props, State> {

  static getEnvironmentFromUrl() {
    const urlArray = window.location.hash.split('/')
    const urlLastElement = urlArray[urlArray.length - 1]
    return urlLastElement !== 'cloudhub' ? urlLastElement : undefined
  }

  constructor(props: Props) {
    super(props)

    this.state = {
      applyModalOpened: false,
      disableModalOpened: false,
      amEnabled: false,
      selectedApp: undefined,
      lastSelectedEnvironment: undefined,
      intervalId: undefined,
      resources: []
    }

    // Environment selector bindings
    this.getEnvironmentSelectorProps = this.getEnvironmentSelectorProps.bind(this)
    this.onEnvironmentSelection = this.onEnvironmentSelection.bind(this)
    this.fetchResourcesInterval = this.fetchResourcesInterval.bind(this)

    // Search bar bindings
    this.getResourcesSearchBarProps = this.getResourcesSearchBarProps.bind(this)
    this.onChangeSearchBar = this.onChangeSearchBar.bind(this)

    // Resources list bindings
    this.getResourcesListProps = this.getResourcesListProps.bind(this)
    this.onEnableCheckbox = this.onEnableCheckbox.bind(this)
    this.onApply = this.onApply.bind(this)

    // Modal bindings
    this.getApplyModalProps = this.getApplyModalProps.bind(this)
    this.handleApplyModalClose = this.handleApplyModalClose.bind(this)
    this.handleApplyModalConfirmation = this.handleApplyModalConfirmation.bind(this)

    this.getDisableModalProps = this.getDisableModalProps.bind(this)
    this.handleDisableModalClose = this.handleDisableModalClose.bind(this)
    this.handleDisableModalConfirmation = this.handleDisableModalConfirmation.bind(this)

    // Toast bindings
    this.getToastProps = this.getToastProps.bind(this)

    this.changeDeploymentEnabled = this.changeDeploymentEnabled.bind(this)
    this.isNewAppsAgentEnabled = this.isNewAppsAgentEnabled.bind(this)
  }

  // Environment selector properties & methods
  getEnvironmentSelectorProps(): EnvironmentSelectorProps {
    return {
      environmentsLoading: this.props.environmentsLoading,
      environments: this.props.environments,
      selectedEnvironment: this.props.selectedEnvironment,
      onEnvironmentSelection: this.onEnvironmentSelection
    }
  }

  onEnvironmentSelection(result: { value: string }) {
    if (this.state.intervalId) clearInterval(this.state.intervalId)
    this.props.selectEnvironment(result.value)
    this.fetchResourcesInterval(result.value)
  }

  fetchResourcesInterval(environmentName: string) {
    if (this.state.lastSelectedEnvironment && this.state.intervalId) {
      clearInterval(this.state.intervalId)
      const intervalId = setInterval(this.props.fetchResources.bind(this, environmentName, false), intervalTime)
      this.setState({lastSelectedEnvironment: environmentName, intervalId: intervalId})
    } else {
      const intervalId = setInterval(this.props.fetchResources.bind(this, environmentName, false), intervalTime)
      this.setState({lastSelectedEnvironment: environmentName, intervalId: intervalId})
    }
  }

  // Resources list properties & methods
  getResourcesSearchBarProps(): ResourcesSearchBarProps {
    return {
      environmentName: this.props.selectedEnvironment ? this.props.selectedEnvironment.name : '',
      onChange: this.onChangeSearchBar,
      query: this.props.query
    }
  }

  onChangeSearchBar(result: any) {
    this.props.filterResources(result.value)
  }

  // Resources list properties & methods
  getResourcesListProps(): ResourcesListProps {
    return {
      filteredResources: SettingsService.mergeMonitoringChecked(this.props.filteredResources, this.state.resources),
      isLoadingResources: this.props.isLoadingResources,
      onEnableCheckbox: this.onEnableCheckbox,
      onApply: this.onApply
    }
  }

  changeDeploymentEnabled(e: { value: boolean }) {
    this.props.postCloudHubSettings(e.value)
  }

  isNewAppsAgentEnabled(): boolean {
    return this.props.isNewAppsAgentEnabled !== undefined ? this.props.isNewAppsAgentEnabled : this.props.isTitanium
  }

  getEnableCheckboxProps(): EnableNewCHAppsProps {
    const enable = this.isNewAppsAgentEnabled()
    return {
      isLoading: this.props.isLoadingCHSettings,
      isChanging: this.props.isChangingCHSettings,
      newAppsAgentEnabled: enable,
      onChangeEnabledCheckbox: this.changeDeploymentEnabled
    }
  }

  onEnableCheckbox(id: string) {
    const apps = SettingsService.mergeMonitoringChecked(this.props.filteredResources, this.state.resources)
    if (apps) {
      const appIndex = apps.findIndex((app => app.name === id))
      const updatedApp = {...apps[appIndex], monitoringChecked: !apps[appIndex].monitoringChecked}
      const updatedApps = [
        ...apps.slice(0, appIndex),
        updatedApp,
        ...apps.slice(appIndex + 1)
      ]
      this.setState({resources: updatedApps})
    }
  }

  onApply(amEnabled: boolean, selectedApp: SettingsApplication) {
    this.setState({amEnabled: amEnabled, selectedApp: selectedApp})

    if (selectedApp && selectedApp.status !== 'STARTED') {
      this.props.applyMonitoringChanges(amEnabled, selectedApp)
    } else if (amEnabled) {
      this.setState({disableModalOpened: true})
    } else {
      this.setState({applyModalOpened: true})
      this.props.checkedMonitoring(selectedApp.name)
    }
  }

  // Modal properties and methods
  getApplyModalProps(): ModalProps {
    return {
      opened: this.state.applyModalOpened,
      onClose: this.handleApplyModalClose,
      onApply: this.handleApplyModalConfirmation
    }
  }

  handleApplyModalClose() {
    this.setState({applyModalOpened: false})
  }

  handleApplyModalConfirmation() {
    if (this.state.selectedApp) this.props.applyMonitoringChanges(this.state.amEnabled, this.state.selectedApp)
    this.handleApplyModalClose()
  }

  getDisableModalProps(): ModalProps {
    return {
      opened: this.state.disableModalOpened,
      onClose: this.handleDisableModalClose,
      onApply: this.handleDisableModalConfirmation
    }
  }

  handleDisableModalClose() {
    this.setState({disableModalOpened: false})
  }

  handleDisableModalConfirmation() {
    if (this.state.selectedApp) this.props.applyMonitoringChanges(this.state.amEnabled, this.state.selectedApp)
    this.handleDisableModalClose()
  }

  // Toast props & methods
  getToastProps(): ToastProps {
    return {
      success: this.props.applyStatus ? this.props.applyStatus.success : false,
      apply: this.props.applyStatus ? this.props.applyStatus.apply : false,
      timestamp: this.props.applyStatus ? this.props.applyStatus.timestamp : undefined,
      application: this.props.applyStatus ? this.props.applyStatus.application : undefined
    }
  }

  getToastByEnableMonitoringProps(): ToastProps {
    return {
      success: this.props.applyEnableMonitoringStatus ? this.props.applyEnableMonitoringStatus.success : false,
      apply: this.props.applyEnableMonitoringStatus ? this.props.applyEnableMonitoringStatus.apply : false,
      timestamp: this.props.applyEnableMonitoringStatus ? this.props.applyEnableMonitoringStatus.timestamp : undefined
    }
  }

  componentWillMount() {
    if (this.props.selectedEnvironment) this.onEnvironmentSelection({value: this.props.selectedEnvironment.id})
  }

  // Render methods
  componentDidMount() {
    if (!this.props.environments) this.props.fetchEnvironments()
    const enable = this.isNewAppsAgentEnabled()
    this.props.fetchCloudHubSettings(!!enable)
  }

  componentWillUnmount() {
    if (this.state.intervalId) clearInterval(this.state.intervalId)
  }

  render() {
    const {environments, selectedEnvironment} = this.props
    const environmentSelectorProps = this.getEnvironmentSelectorProps()
    const resourcesSearchBarProps = this.getResourcesSearchBarProps()
    const resourcesListProps = this.getResourcesListProps()
    const enableNewCHAppsCheckboxProps = this.getEnableCheckboxProps()

    const applyModalProps = this.getApplyModalProps()
    const disableModalProps = this.getDisableModalProps()

    const toastProps = this.getToastProps()
    const toastEnableMonitoringProps = this.getToastByEnableMonitoringProps()

    const checkboxEnabled = window.mc.featureFlags.settings.checkbox.enabled === 'true'

    if (environments && environments.length > 0) {
      const envFromURL = CloudHubSettingsPage.getEnvironmentFromUrl()
      if (selectedEnvironment && !envFromURL) {
        window.location.hash = `#/settings/cloudhub/environments/${selectedEnvironment.id}`
      } else if (!selectedEnvironment && envFromURL && environments.find(env => env.id === envFromURL)) {
        this.onEnvironmentSelection({value: envFromURL})
      } else if (!selectedEnvironment && envFromURL && !environments.find(env => env.id === envFromURL)) {
        window.location.hash = `#/settings/cloudhub/`
      }
    }

    return (
      <div className="cloudhub-settings-page" data-test-id="cloudhub-settings-page">
        {
          checkboxEnabled &&
          <div>
              <NewAppsCloudHubEnableCheckbox {...enableNewCHAppsCheckboxProps}/>
              <div className="cloudhub-settings-page-title-container" data-test-id="cloudhub-settings-page-title-container">
                  <h4 className="cloudhub-settings-page-title" data-test-id="cloudhub-settings-page-title">
                      Enable/disable Anypoint Monitoring for individual applications
                  </h4>
                  <span className="cloudhub-settings-page-subtitle" data-test-id="cloudhub-settings-page-subtitle">
                      Enable or disable Anypoint Monitoring for specific applications.
                  </span>
              </div>
          </div>
        }
        <SettingsBodyEnvironmentSelector {...environmentSelectorProps} />
        {this.props.selectedEnvironment && <SettingsBodyResourcesListHeader {...resourcesSearchBarProps}/>}
        {this.props.selectedEnvironment && <SettingsBodyResourcesListBody {...resourcesListProps}/>}
        <SettingsBodyApplyModal {...applyModalProps}/>
        <SettingsBodyDisableModal {...disableModalProps}/>
        <SettingsBodyToast {...toastProps}/>
        <SettingsBodyToast {...toastEnableMonitoringProps}/>
      </div>
    )
  }
}
